'use client';

import { isAfter, isBefore } from 'date-fns';
import type { Route } from 'next';
import { useRouter } from 'next/navigation';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import TextAlert from '../../../../components/ui/TextAlert/TextAlert';
import { Format } from '../../../../lib/dateFns';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { newDate } from '../../../../utils/date';
import { formatDateCommon } from '../../../../utils/formatters';
import { isVersion } from '../../../../utils/version';
import type { LotteryHistoryCollection } from '../../types';
import styles from './LotteryCollectionHistories.module.scss';

function LotteryCollectionHistories({
  historyList,
  orderId,
}: {
  historyList: LotteryHistoryCollection[];
  orderId?: string | null;
}) {
  const router = useRouter();

  const getHistoryUrl = (item: LotteryHistoryCollection): Route => {
    const baseUrl = `/account/collection/histories/${item.id}`;
    const orderIdToUse = item.orderId ? item.orderId.toString() : orderId;
    if (orderIdToUse) {
      return `${baseUrl}?orderId=${orderIdToUse}` as Route;
    }
    return baseUrl as Route;
  };

  const shouldShowOptionInputButton = (item: LotteryHistoryCollection): boolean => {
    if (item.hasPrizeOptionFlag !== true) return false;
    if (!isVersion(item.version, '2.0')) return false;
    if (!item.optionCutoffAt) return false;
    if (!isAfter(newDate(item.optionCutoffAt), newDate())) return false;
    return true;
  };

  const shouldShowOptionViewButton = (item: LotteryHistoryCollection): boolean => {
    if (item.hasPrizeOptionFlag !== true) return false;
    if (!isVersion(item.version, '2.0')) return false;
    if (!item.optionCutoffAt) return false;
    if (!isBefore(newDate(item.optionCutoffAt), newDate())) return false;
    return true;
  };

  const goInputOption = (e: React.MouseEvent, item: LotteryHistoryCollection) => {
    e.preventDefault();
    e.stopPropagation();
    const orderIdToUse = item.orderId ? item.orderId.toString() : orderId;
    const params = new URLSearchParams();
    params.set('lotteryHistoryId', item.id.toString());
    if (orderIdToUse) {
      params.set('orderId', orderIdToUse);
    }
    router.push(`/account/collection/option?${params.toString()}` as Route);
  };

  return (
    <ul className={styles.lotteryCollectionEvents}>
      {historyList.map((item: LotteryHistoryCollection, index: number) => (
        <li key={`${item.lotteryEventId}-${item.id}-${index}`}>
          <div
            className={styles.itemLink}
            onClick={() => {
              router.push(getHistoryUrl(item));
            }}
          >
            <div className={styles.itemCard}>
              <div className={styles.itemImage}>
                <ImageView alt={item.lotteryEventName} src={item.thumbnailUrl} isFreeSize={true} />
              </div>
              <div className={styles.itemDetail}>
                {item.orderId && (
                  <p
                    className={`${utilsStyles.textBold} ${utilsStyles.textSmall} ${utilsStyles.textRight} ${utilsStyles.onlyPc}`}
                  >
                    Order ID:&nbsp;{item.orderId}
                  </p>
                )}
                <h2 className={`${styles.itemTitle} ${utilsStyles.textBold}`}>{item.lotteryEventName}</h2>
                {item.drawType !== 'trade' && (
                  <div className={`${styles.additionalInfoContainer} ${utilsStyles.textSmall}`}>
                    <p>くじ引き回数:&nbsp;{item.drawCount}</p>
                    <p>抽選日時:&nbsp;{formatDateCommon(item.createdAt, Format.dateTimeSlash)}</p>
                  </div>
                )}
                {item.drawType === 'trade' && (
                  <div className={`${styles.additionalInfoContainer} ${utilsStyles.textSmall}`}>
                    <p>景品交換</p>
                    <p>交換日時:&nbsp;{formatDateCommon(item.createdAt, Format.dateTimeSlash)}</p>
                  </div>
                )}
              </div>
            </div>
            {item.incompleteOptionFlag && (
              <div
                className={`${utilsStyles.mtXSmall} ${utilsStyles.textXLarge} ${utilsStyles.textBold} ${utilsStyles.textCenter}`}
              >
                <TextAlert>
                  {/* アイコンとテキストの間に半角空白一文字の対応を入れたいため、半角空白を入れている */}
                  &nbsp;オプション入力が必要な景品があります
                  <br />
                  <span>(入力期限：{formatDateCommon(item.optionCutoffAt, Format.dateTimeSlash)}まで)</span>
                </TextAlert>
              </div>
            )}
            {shouldShowOptionInputButton(item) && (
              <div className={`${utilsStyles.mtXSmall}`}>
                {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
                <a href='#' onClick={(e) => goInputOption(e, item)}>
                  <button type='button' className={`${styles.optionButton}`}>
                    オプションを入力する
                  </button>
                </a>
              </div>
            )}

            {shouldShowOptionViewButton(item) && (
              <div className={`${utilsStyles.mtSmall}`}>
                {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
                <a href='#' onClick={(e) => goInputOption(e, item)}>
                  <button type='button' className={`${styles.actionButton}`}>
                    入力済みのオプションを確認する ※編集不可
                  </button>
                </a>
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default LotteryCollectionHistories;
